<template>
  <div class="history-news">
    <div class="section-header">
      <!-- 左侧：标题 -->
      <div class="header-left">
      <div class="section-icon">📰</div>
      <h2 class="section-title">全球科技头条</h2>
      </div>
      
      <!-- 中间：音频进度条 -->
      <div class="header-center">
      <div class="audio-progress-container" v-if="audioInfo && audioInfo.audio_url">
        <div class="progress-wrapper">
          <div 
            class="progress-bar" 
            ref="progressBar"
            @click="seekAudio"
          >
            <div 
              class="progress-fill" 
              :style="{ width: progressPercent + '%' }"
            ></div>
            <div 
              class="progress-handle" 
              :style="{ left: progressPercent + '%' }"
              @mousedown="startDrag"
            ></div>
          </div>
          <div class="time-display">
            <span class="current-time">{{ formatTime(currentTime) }}</span>
            <span class="duration">{{ formatTime(duration) }}</span>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 右侧：播放按钮 -->
      <div class="header-right">
      <div class="audio-controls" v-if="audioInfo && audioInfo.audio_url">
        <button 
          class="play-btn" 
          :class="{ playing: isPlaying, loading: audioLoading }"
          @click="toggleAudio"
          :disabled="audioLoading"
        >
          <i v-if="audioLoading" class="el-icon-loading"></i>
          <i v-else-if="isPlaying" class="el-icon-video-pause"></i>
          <i v-else class="el-icon-video-play"></i>
        </button>
        </div>
      </div>
    </div>
    
    <!-- 统一日期选择器 -->
    <div class="date-selector" v-if="!isKeywordSearch">
      <div class="date-selector-container" v-if="!isKeywordSearch && !isMajorSearch">
        <button class="unified-date-btn" @click="showDatePopup = !showDatePopup">
          <i class="el-icon-date"></i>
          <span class="selected-date-text">{{ formatSelectedDate }}</span>
          <i class="el-icon-arrow-down" :class="{ 'rotate': showDatePopup }"></i>
        </button>
        
        <!-- 日期选择弹窗 -->
        <div v-if="showDatePopup" class="date-popup" @click.stop>
          <div class="date-popup-content">
            <div class="date-popup-header">
              <span>选择日期</span>
              <button class="close-btn" @click="showDatePopup = false">
                <i class="el-icon-close"></i>
              </button>
            </div>
            
            <!-- 年月选择 -->
            <div class="year-month-selector">
              <select v-model="selectedYear" @change="onYearMonthChange" class="year-select">
                <option v-for="year in availableYears" :key="year" :value="year">
                  {{ year }}年
                </option>
              </select>
              <select v-model="selectedMonth" @change="onYearMonthChange" class="month-select">
                <option v-for="month in availableMonthsForYear" :key="month" :value="month">
                  {{ month }}月
                </option>
              </select>
            </div>
            
            <!-- 日期网格 -->
            <div class="date-grid">
              <div 
                v-for="date in datesInSelectedMonth" 
                :key="date.value"
                class="date-grid-item"
                :class="{ 
                  active: selectedDate === date.value, 
                  yesterday: date.isYesterday,
                  today: date.isToday,
                  disabled: date.disabled
                }"
                @click="!date.disabled && selectDateFromPopup(date.value)"
              >
                <div class="date-number">{{ date.day }}</div>
                <div class="date-label">{{ date.label }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 关键词搜索模式显示 -->
    <div class="keyword-search-header" v-if="isKeywordSearch">
      <div class="keyword-search-container">
        <div class="keyword-search-box">
          <button class="back-btn" @click="exitKeywordSearch">
            <i class="el-icon-back"></i>
            <span>返回</span>
          </button>
          <input 
            v-model="searchInput"
            type="text" 
            class="keyword-input"
            placeholder="输入关键词搜索..."
            @keyup.enter="executeSearch"
          />
          <i class="el-icon-search search-icon" @click="executeSearch"></i>
        </div>
      </div>
    </div>

    <!-- 专业分类搜索模式显示 -->
    <div class="major-search-header" v-if="isMajorSearch">
      <div class="major-search-container">
        <div class="major-search-box">
          <button class="back-btn" @click="exitMajorSearch">
            <i class="el-icon-back"></i>
            <span>返回</span>
          </button>
          <input 
            v-model="searchInput"
            type="text" 
            class="major-input"
            placeholder="输入专业分类搜索..."
            @keyup.enter="executeMajorSearch"
          />
          <i class="el-icon-search search-icon" @click="executeMajorSearch"></i>
        </div>
      </div>
    </div>

    <div class="content-wrapper">
      <!-- 新闻内容（全宽） -->
      <div class="news-content" ref="newsContent">
        <div v-if="loading" class="loading-state">
          <i class="el-icon-loading"></i>
          <span>加载中...</span>
        </div>
        
        <div v-else-if="newsList.length === 0" class="empty-state">
          <div class="empty-icon">📰</div>
          <p>该日期暂无新闻</p>
        </div>
        
        <div v-else>
          <div class="news-list">
          <div 
            v-for="news in newsList" 
            :key="news.news_id"
            class="news-item"
            :class="{ 
              expanded: expandedNews === news.news_id,
              'has-image': news.news_image
            }"
          >
            <!-- 有图片的新闻：折叠状态显示标题，展开状态显示图片和叠加内容 -->
            <template v-if="news.news_image">
              <!-- 折叠状态：只显示标题 -->
              <div v-if="expandedNews !== news.news_id" class="news-header">
                <h3 class="news-title" @click="toggleNews(news.news_id)">{{ news.news_title }}</h3>
                <div class="news-meta">
                  <!-- 在关键词搜索模式或专业分类搜索模式下显示时间 -->
                  <span 
                    v-if="isKeywordSearch || isMajorSearch" 
                    class="news-time clickable-time" 
                    @click="handleTimeClick(news.created_at || news.published_at || news.time_published || news.publish_time || news.news_time || news.date || news.time, $event)"
                  >
                    {{ formatDateDisplay(news.created_at || news.published_at || news.time_published || news.publish_time || news.news_time || news.date || news.time || '未知时间') }}
                  </span>
                  <i class="el-icon-arrow-down expand-icon" @click="toggleNews(news.news_id)"></i>
                </div>
              </div>
              
              <!-- 展开状态：显示图片和叠加内容 -->
              <div v-else class="news-image-container" @click="toggleNews(news.news_id)">
                <img :src="news.news_image" :alt="news.news_title" class="news-background-image" />
                <div class="news-overlay" @click.stop>
                  <div class="news-overlay-content">
                    <h3 class="overlay-title" @click="openNewsDetail(news, $event)">{{ news.news_title }}</h3>
                    <div class="overlay-content">{{ news.news_content }}</div>
                    <div class="overlay-tags">
                      <!-- 左侧：分类标签和关键词标签 -->
                      <div class="overlay-tags-left">
                        <!-- 分类标签 -->
                        <div v-if="news.category && news.category.length > 0" class="overlay-categories">
                          <span 
                            v-for="cat in news.category" 
                            :key="cat.id" 
                            class="category-tag"
                            @click.stop="handleMajorClick(cat.major_id, cat.major_name)"
                          >
                            {{ cat.major_name }}
                          </span>
                        </div>
                        <!-- 关键词标签 -->
                        <div v-if="news.book_keywords_list && news.book_keywords_list.length > 0" class="overlay-keywords">
                          <span 
                            v-for="item in news.book_keywords_list" 
                            :key="item.book_keywords_id"
                            class="keyword"
                            @click.stop="handleKeywordClick(item.book_keywords_id, item.keywords)"
                          >
                            {{ item.keywords }}
                          </span>
                        </div>
                      </div>
                      <!-- 右侧：时间显示 -->
                      <div v-if="isKeywordSearch || isMajorSearch" class="overlay-time">
                        <span 
                          class="time-info clickable-time" 
                          @click="handleTimeClick(news.created_at || news.published_at || news.time_published || news.publish_time || news.news_time || news.date || news.time, $event)"
                        >
                          {{ formatDateDisplay(news.created_at || news.published_at || news.time_published || news.publish_time || news.news_time || news.date || news.time || '未知时间') }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="collapse-icon" @click="toggleNews(news.news_id)">
                    <i class="el-icon-arrow-up"></i>
                  </div>
                </div>
              </div>
            </template>
            
            <!-- 无图片的新闻：修改为需要展开才能跳转 -->
            <template v-else>
              <div class="news-header">
                <h3 class="news-title" @click="toggleNews(news.news_id)">{{ news.news_title }}</h3>
                <div class="news-meta">
                  <!-- 在关键词搜索模式或专业分类搜索模式下显示时间 -->
                  <span 
                    v-if="isKeywordSearch || isMajorSearch" 
                    class="news-time clickable-time" 
                    @click="handleTimeClick(news.created_at || news.published_at || news.time_published || news.publish_time || news.news_time || news.date || news.time, $event)"
                  >
                    {{ formatDateDisplay(news.created_at || news.published_at || news.time_published || news.publish_time || news.news_time || news.date || news.time || '未知时间') }}
                  </span>
                  <i class="el-icon-arrow-down expand-icon" @click="toggleNews(news.news_id)"></i>
                </div>
              </div>
              
              <div v-if="expandedNews === news.news_id" class="news-detail">
                <div class="news-content-text">
                  {{ news.news_content }}
                </div>
                <div class="news-actions">
                  <h3 class="clickable-title" @click="openNewsDetail(news, $event)">{{ news.news_title }}</h3>
                </div>
                <div class="news-tags">
                  <!-- 分类标签 -->
                  <div v-if="news.category && news.category.length > 0" class="news-categories">
                    <span 
                      v-for="cat in news.category" 
                      :key="cat.id" 
                      class="category-tag"
                      @click.stop="handleMajorClick(cat.major_id, cat.major_name)"
                    >
                      {{ cat.major_name }}
                    </span>
                  </div>
                  <!-- 关键词标签 -->
                  <div v-if="news.book_keywords_list && news.book_keywords_list.length > 0" class="news-keywords">
                    <span 
                      v-for="item in news.book_keywords_list" 
                      :key="item.book_keywords_id"
                      class="keyword"
                      @click.stop="handleKeywordClick(item.book_keywords_id, item.keywords)"
                    >
                      {{ item.keywords }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
          
          <!-- 加载更多提示 -->
          <div v-if="isLoadingMore" class="loading-more">
            <i class="el-icon-loading"></i>
            <span>加载中...</span>
          </div>
          
          <!-- 没有更多数据提示 -->
          <div v-if="!hasMore && newsList.length > 0" class="no-more">
            <span>已加载全部新闻</span>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 点击外部关闭弹窗 -->
    <div v-if="showDatePopup" class="popup-overlay" @click="showDatePopup = false"></div>
    
    <!-- 关键词时间轴弹窗 -->
    <KeywordsTimelineDialog
      :visible.sync="showKeywordsTimelineDialog"
      :book-keywords-id="selectedBookKeywordsId"
      :keyword-name="selectedKeywordName"
    />
  </div>
</template>

<script>
import { getHistoryAiNewsList, getBookKeywordsTimeline } from '@/http/api/news'
import KeywordsTimelineDialog from './KeywordsTimelineDialog.vue'

export default {
  name: 'HistoryNews',
  components: {
    KeywordsTimelineDialog
  },
  data() {
    return {
      loading: false,
      newsList: [],
      expandedNews: null,
      selectedDate: '',
      selectedYear: new Date().getFullYear(),
      selectedMonth: new Date().getMonth() + 1,
      showDatePopup: false,
      audioInfo: null, // 存储当前使用的音频信息
      globalAudioInfo: null, // 存储全局音频信息（从接口返回的audio_info）
      // 音频播放相关状态
      audio: null,
      currentAudioUrl: null, // 当前正在加载的音频URL，用于错误处理时验证
      audioEventHandlers: null, // 保存事件处理函数引用，用于清理
      isPlaying: false,
      audioLoading: false,
      currentTime: 0,
      duration: 0,
      isDragging: false,
      // 关键词搜索模式
      isKeywordSearch: false,
      currentKeyword: '',
      currentKeywordId: null, // 当前关键词ID
      // 专业分类搜索模式
      isMajorSearch: false,
      currentMajor: '',
      searchInput: '', // 搜索输入框的内容
      // 分页加载相关
      currentPage: 1,
      pageSize: 20,
      hasMore: true,
      isLoadingMore: false,
      // 关键词时间轴弹窗
      showKeywordsTimelineDialog: false,
      selectedBookKeywordsId: null,
      selectedKeywordName: ''
    }
  },
  computed: {
    // 进度百分比
    progressPercent() {
      if (!this.duration || this.duration === 0) return 0
      return (this.currentTime / this.duration) * 100
    },

    // 格式化选中的日期显示
    formatSelectedDate() {
      // 如果是关键词搜索模式，显示关键词名称
      if (this.isKeywordSearch && this.currentKeyword) {
        return this.currentKeyword
      }
      
      if (!this.selectedDate) return '选择日期'
      const date = new Date(this.selectedDate)
      const year = date.getFullYear()
      const month = date.getMonth() + 1
      const day = date.getDate()
      
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      
      if (this.isSameDate(date, today)) {
        return `${year}年${month}月${day}日 (今天)`
      } else if (this.isSameDate(date, yesterday)) {
        return `${year}年${month}月${day}日 (昨天)`
      } else {
        const weekdays = ['日', '一', '二', '三', '四', '五', '六']
        return `${year}年${month}月${day}日 (周${weekdays[date.getDay()]})`
      }
    },
    
    // 可选择的年份列表（最近2年）
    availableYears() {
      const currentYear = new Date().getFullYear()
      return [currentYear, currentYear - 1]
    },
    
    // 当前年份可选择的月份列表
    availableMonthsForYear() {
      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth() + 1
      
      if (this.selectedYear === currentYear) {
        // 如果是当前年份，只显示到当前月份
        const months = []
        for (let i = 1; i <= currentMonth; i++) {
          months.push(i)
        }
        return months.reverse() // 倒序显示，最新的月份在前
      } else {
        // 如果是过去年份，显示全年12个月
        return [12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1]
      }
    },
    
    // 选中年月的日期列表
    datesInSelectedMonth() {
      const year = this.selectedYear
      const month = this.selectedMonth
      
      // 获取当月天数
      const daysInMonth = new Date(year, month, 0).getDate()
      const dates = []
      
      // 获取今天和昨天的日期
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      
      for (let day = 1; day <= daysInMonth; day++) {
        const date = new Date(year, month - 1, day)
        const dateStr = this.formatDateToString(date)
        const isToday = this.isSameDate(date, today)
        const isYesterday = this.isSameDate(date, yesterday)
        const isFuture = date > today
        
        dates.push({
          day: day.toString().padStart(2, '0'),
          value: dateStr,
          label: this.getDateLabel(date),
          isToday,
          isYesterday,
          disabled: isFuture // 禁用未来日期
        })
      }
      
      return dates.reverse() // 倒序显示，最新的日期在前
    }
  },
  mounted() {
    this.initializeData()
    this.addScrollListener()
  },
  methods: {
    // 将逗号分隔的关键词字符串转换为数组
    getKeywordList(keywords) {
      if (!keywords) return []
      return keywords.split(',').map(keyword => keyword.trim()).filter(keyword => keyword.length > 0)
    },

    // 初始化数据
    initializeData() {
      this.setDefaultDate()
      this.loadNews()
    },

    // 年月选择变化时的处理
    onYearMonthChange() {
      // 年月变化时，如果当前选中的日期不在新的年月中，则重置选中日期
      const currentSelectedDate = new Date(this.selectedDate)
      if (currentSelectedDate.getFullYear() !== this.selectedYear || 
          currentSelectedDate.getMonth() + 1 !== this.selectedMonth) {
        // 选择该月的第一个可用日期
        const availableDates = this.datesInSelectedMonth.filter(date => !date.disabled)
        if (availableDates.length > 0) {
          this.selectedDate = availableDates[0].value
          // 切换日期时重置音频
          this.resetAudio()
          this.audioInfo = null
          this.globalAudioInfo = null
          this.loadNews()
        }
      }
    },

    // 获取日期标签
    getDateLabel(date) {
      const today = new Date()
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)
      
      if (this.isSameDate(date, today)) {
        return '今天'
      } else if (this.isSameDate(date, yesterday)) {
        return '昨天'
      } else {
        const weekdays = ['日', '一', '二', '三', '四', '五', '六']
        return `周${weekdays[date.getDay()]}`
      }
    },

    // 判断是否为同一天
    isSameDate(date1, date2) {
      return date1.getFullYear() === date2.getFullYear() &&
             date1.getMonth() === date2.getMonth() &&
             date1.getDate() === date2.getDate()
    },

    // 格式化日期为字符串
    formatDateToString(date) {
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    },

    // 设置默认日期（今天）
    setDefaultDate() {
      const today = new Date()
      this.selectedDate = this.formatDateToString(today)
      this.selectedYear = today.getFullYear()
      this.selectedMonth = today.getMonth() + 1
    },

    // 选择日期
    selectDate(date) {
      this.selectedDate = date
      this.expandedNews = null // 关闭展开的新闻
      this.currentPage = 1
      this.hasMore = true
      this.newsList = []
      this.loadNews()
    },

    // 从弹窗中选择日期
    selectDateFromPopup(date) {
      this.selectedDate = date
      this.expandedNews = null // 关闭展开的新闻
      this.showDatePopup = false // 关闭弹窗
      this.currentPage = 1
      this.hasMore = true
      this.newsList = []
      // 切换日期时重置音频
      this.resetAudio()
      this.audioInfo = null
      this.globalAudioInfo = null
      this.loadNews()
    },

    // 加载新闻数据
    async loadNews() {
      if (!this.selectedDate || !this.hasMore || this.isLoadingMore) return
      
      this.loading = this.currentPage === 1
      this.isLoadingMore = this.currentPage > 1
      
      try {
        const params = {
          history_date: this.selectedDate,
          page: this.currentPage,
          limit: this.pageSize
        }
        const response = await getHistoryAiNewsList(params)
        
        // 适配新的接口结构: {code: 200, result: {list: [], audio_info: {}}, message: ""}
        // request.js在code=200时会直接返回result部分
        let newsList = []
        if (response && response.list && Array.isArray(response.list)) {
          // 新接口结构：response.list 是新闻数组
          newsList = response.list
          
          // 第一页时，如果有音频信息，存储到组件中
          if (this.currentPage === 1 && response.audio_info) {
            this.globalAudioInfo = response.audio_info
            // 如果没有展开的新闻，使用全局音频
            if (!this.expandedNews) {
              this.audioInfo = response.audio_info
            }
          }
        } else if (Array.isArray(response)) {
          // 兼容旧的接口结构（直接返回数组）
          newsList = response
        } else if (response && response.result && Array.isArray(response.result)) {
          // 兼容其他可能的结构
          newsList = response.result
        }
        
        // 判断是否还有更多数据
        if (newsList.length < this.pageSize) {
          this.hasMore = false
        }
        
        // 追加新闻到列表
        if (this.currentPage === 1) {
          this.newsList = newsList
          // 默认打开第一条新闻
          if (this.newsList.length > 0) {
            const firstNews = this.newsList[0]
            this.expandedNews = firstNews.news_id
            // 检查第一条新闻的音频
            const audioUrl = firstNews.audio_mp3 || firstNews.audio_url
            if (audioUrl) {
              this.audioInfo = { audio_url: audioUrl }
              // 自动播放音频
              this.$nextTick(() => {
                this.initAudio(true)
              })
            } else {
              this.audioInfo = this.globalAudioInfo
              // 如果有全局音频，自动播放
              if (this.globalAudioInfo?.audio_url) {
                this.$nextTick(() => {
                  this.initAudio(true)
                })
              }
            }
          }
        } else {
          this.newsList = [...this.newsList, ...newsList]
        }
        
      } catch (error) {
        if (this.currentPage === 1) {
        this.newsList = []
        }
        
        if (error.message && error.message.includes('1003')) {
          this.$message.error('登录已过期，科技新闻不应该需要登录认证')
        } else {
          this.$message.error('网络错误，请稍后重试')
        }
      } finally {
        this.loading = false
        this.isLoadingMore = false
      }
    },

    // 规范化URL（用于比较）
    normalizeUrlForCompare(url) {
      if (!url) return ''
      try {
        const urlObj = new URL(url.trim())
        const normalizedPath = urlObj.pathname.replace(/\/+/g, '/')
        return `${urlObj.protocol}//${urlObj.host}${normalizedPath}${urlObj.search}${urlObj.hash}`
      } catch {
        return url.trim()
      }
    },

    // 检查是否应该继续播放当前音频（不打断）
    shouldContinuePlaying(newUrl, hasAudioMp3 = false) {
      if (!newUrl) return false
      // 如果有 audio_mp3，总是重新加载
      if (hasAudioMp3) return false
      
      const normalizedUrl = this.normalizeUrlForCompare(newUrl)
      const currentUrl = this.currentAudioUrl || (this.audio?.currentSrc ? this.normalizeUrlForCompare(this.audio.currentSrc) : '')
      
      // 如果URL相同且正在播放，则继续播放不打断
      return normalizedUrl === currentUrl && this.isPlaying && this.audio
    },

    // 切换新闻展开状态
    async toggleNews(newsId) {
      const isExpanding = this.expandedNews !== newsId
      this.expandedNews = this.expandedNews === newsId ? null : newsId
      
      // 当展开新闻时，检查该新闻的音频
      if (isExpanding) {
        const news = this.newsList.find(item => item.news_id === newsId)
        if (news) {
          // 优先使用 audio_mp3，其次使用 audio_url
          const audioUrl = news.audio_mp3 || news.audio_url
          if (audioUrl) {
            // 如果只有 audio_url 且URL相同，且正在播放，则继续播放不打断
            if (this.shouldContinuePlaying(audioUrl, !!news.audio_mp3)) {
              return
            }
            
            // 重置当前音频
            this.resetAudio()
            // 设置当前新闻的音频信息
            this.audioInfo = { audio_url: audioUrl }
            // 初始化音频并自动播放
            await this.initAudio(true)
          } else {
            // 如果新闻没有音频，使用全局音频
            const globalUrl = this.globalAudioInfo?.audio_url
            if (globalUrl && this.shouldContinuePlaying(globalUrl)) {
              return
            }
            
            this.resetAudio()
            this.audioInfo = this.globalAudioInfo
            // 如果有全局音频，自动播放
            if (this.globalAudioInfo?.audio_url) {
              await this.initAudio(true)
            }
          }
        }
      } else {
        // 收起新闻时，恢复全局音频
        const globalUrl = this.globalAudioInfo?.audio_url
        if (globalUrl && this.shouldContinuePlaying(globalUrl)) {
          return
        }
        
        this.resetAudio()
        this.audioInfo = this.globalAudioInfo
        // 如果有全局音频，自动播放
        if (this.globalAudioInfo?.audio_url) {
          await this.initAudio(true)
        }
      }
    },

    // 格式化时间显示（用于新闻时间）
    formatNewsTime(timeStr) {
      if (!timeStr) return ''
      const date = new Date(timeStr)
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      return `${hours}:${minutes}`
    },

    // 格式化日期显示（显示年月日格式）
    formatDateDisplay(timeStr) {
      if (!timeStr || timeStr === '未知时间') return '未知时间'
      
      try {
        let date
        
        // 判断是字符串、秒时间戳还是毫秒时间戳
        if (typeof timeStr === 'number') {
          // 如果是数字，判断是秒还是毫秒
          // 如果小于 10000000000（2286年），说明是秒，需要转换为毫秒
          date = new Date(timeStr < 10000000000 ? timeStr * 1000 : timeStr)
        } else if (typeof timeStr === 'string') {
          // 如果是字符串，直接解析
          date = new Date(timeStr)
        } else {
          return '未知时间'
        }
        
        if (isNaN(date.getTime())) {
          return timeStr.toString()
        }
        
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        
        return `${year}-${month}-${day}`
      } catch (error) {
        // 如果时间解析失败，尝试直接显示时间字符串
        return timeStr.toString()
      }
    },

    // 处理时间点击事件，跳转到对应日期的新闻列表
    handleTimeClick(timeStr, event) {
      // 阻止事件冒泡
      if (event) {
        event.stopPropagation()
      }
      
      if (!timeStr || timeStr === '未知时间') return
      
      try {
        const date = new Date(timeStr)
        if (isNaN(date.getTime())) {
          return
        }
        
        // 格式化为 YYYY-MM-DD 格式
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        const dateStr = `${year}-${month}-${day}`
        
        // 退出关键词搜索模式，回到日期模式
        this.isKeywordSearch = false
        this.currentKeyword = ''
        this.searchInput = ''
        
        // 设置选中的日期
        this.selectedDate = dateStr
        this.selectedYear = year
        this.selectedMonth = parseInt(month)
        
        // 重置状态
        this.expandedNews = null
        this.currentPage = 1
        this.hasMore = true
        this.newsList = []
        
        // 重置音频
        this.resetAudio()
        this.audioInfo = null
        this.globalAudioInfo = null
        
        // 加载对应日期的新闻
        this.loadNews()
        
        this.$message.success(`已跳转到 ${dateStr} 的新闻列表`)
      } catch (error) {
        this.$message.error('日期格式错误，无法跳转')
      }
    },

    // 打开新闻详情页面
    openNewsDetail(news, event) {
      // 阻止事件冒泡，避免触发展开/收起功能
      if (event) {
        event.stopPropagation()
      }
      
      // 检查新闻是否有URL链接 - 根据新接口结构，URL存储在source字段中
      const url = news.source || news.news_url || news.url || news.original_url || news.source_url || news.link
      
      if (url && url !== '#' && url !== '' && url !== null && url !== undefined) {
        window.open(url, '_blank')
      } else {
        // 如果没有URL，则切换展开状态
        this.toggleNews(news.news_id)
      }
    },

    // 音频播放控制方法
    async toggleAudio() {
      if (!this.audioInfo || !this.audioInfo.audio_url) return
      
      if (!this.audio) {
        await this.initAudio()
      }
      
      if (this.isPlaying) {
        this.pauseAudio()
      } else {
        this.playAudio()
      }
    },

    // 初始化音频
    async initAudio(autoPlay = false) {
      if (!this.audioInfo?.audio_url) return
      
      this.audioLoading = true
      
      // 清理之前的音频对象
      if (this.audio) {
        this.cleanupAudio()
      }
      
      try {
        // 规范化URL（移除多余的双斜杠）
        let audioUrl = this.audioInfo.audio_url.trim()
        const urlObj = new URL(audioUrl)
        const normalizedPath = urlObj.pathname.replace(/\/+/g, '/')
        audioUrl = `${urlObj.protocol}//${urlObj.host}${normalizedPath}${urlObj.search}${urlObj.hash}`
        
        this.currentAudioUrl = audioUrl
        this.audio = new Audio()
        
        // 事件处理函数
        const handleLoadedMetadata = () => {
          if (this.audio?.currentSrc === this.currentAudioUrl) {
            this.duration = this.audio.duration
            this.audioLoading = false
            // 如果设置了自动播放，则自动播放
            if (autoPlay) {
              // 延迟一小段时间确保音频完全准备好
              setTimeout(() => {
                if (this.audio && this.audio.currentSrc === this.currentAudioUrl) {
                  this.playAudio()
                }
              }, 100)
            }
          }
        }
        
        const handleTimeUpdate = () => {
          if (!this.isDragging && this.audio) {
            this.currentTime = this.audio.currentTime
          }
        }
        
        const handleEnded = () => {
          this.isPlaying = false
          this.currentTime = 0
        }
        
        const handlePlay = () => {
          this.isPlaying = true
        }
        
        const handlePause = () => {
          this.isPlaying = false
        }
        
        const handleError = (e) => {
          const audioElement = e.target || this.audio
          
          // 忽略来自已清理音频对象的错误
          if (audioElement !== this.audio || audioElement.currentSrc !== this.currentAudioUrl) {
            return
          }
          
          this.audioLoading = false
          
          if (audioElement?.error) {
            // 延迟检查音频是否真的无法播放
            setTimeout(() => {
              // 如果音频对象已经被清理或URL已经改变，忽略错误
              if (!this.audio || this.audio !== audioElement || this.currentAudioUrl !== audioElement.currentSrc) {
                return
              }
              
              // 如果音频正在播放，说明错误是临时的，不显示错误消息
              if (this.isPlaying || !audioElement.paused) {
                return
              }
              
              const errorMessages = {
                1: '音频加载被中止',
                2: '网络错误，无法加载音频',
                3: '音频解码失败',
                4: '音频格式不支持或URL无效'
              }
              const errorMessage = errorMessages[audioElement.error.code] || '音频加载失败'
              this.cleanupAudio()
              this.$message.error(errorMessage)
            }, 1000) // 延迟1秒检查，给音频恢复的时间
          }
        }
        
        // 保存事件处理函数引用
        this.audioEventHandlers = {
          loadedmetadata: handleLoadedMetadata,
          timeupdate: handleTimeUpdate,
          ended: handleEnded,
          error: handleError,
          play: handlePlay,
          pause: handlePause
        }
        
        // 添加事件监听器
        Object.keys(this.audioEventHandlers).forEach(event => {
          this.audio.addEventListener(event, this.audioEventHandlers[event])
        })
        
        // 设置音频源并加载
        this.audio.src = audioUrl
        this.audio.load()
        
      } catch (error) {
        this.audioLoading = false
        this.currentAudioUrl = null
        this.$message.error('音频初始化失败: ' + (error.message || '未知错误'))
        this.cleanupAudio()
      }
    },

    // 清理音频对象和事件监听器
    cleanupAudio() {
      if (this.audio) {
        try {
          // 移除事件监听器
          if (this.audioEventHandlers) {
            Object.keys(this.audioEventHandlers).forEach(event => {
              this.audio.removeEventListener(event, this.audioEventHandlers[event])
            })
            this.audioEventHandlers = null
          }
          
          this.audio.pause()
          this.audio.src = ''
        } catch (e) {
          // 忽略清理错误
        }
        this.audio = null
      }
      this.currentAudioUrl = null
    },

    // 播放音频
    async playAudio() {
      if (!this.audio) return
      
      try {
        await this.audio.play()
        this.isPlaying = true
      } catch (error) {
        // 不显示错误提示，因为：
        // 1. 浏览器可能会阻止自动播放，但用户点击后会成功播放
        // 2. play/pause 事件监听器会正确更新 isPlaying 状态
        // 3. 如果真的有错误，error 事件监听器会处理
        // 延迟检查音频是否真的播放失败（静默处理）
        setTimeout(() => {
          // 如果音频已经开始播放，更新状态
          if (this.audio && !this.audio.paused) {
            this.isPlaying = true
          }
        }, 500)
      }
    },

    // 暂停音频
    pauseAudio() {
      if (!this.audio) return
      
      this.audio.pause()
      this.isPlaying = false
    },

    // 跳转到指定时间
    seekAudio(event) {
      if (!this.audio || !this.duration) return
      
      const progressBar = this.$refs.progressBar
      if (!progressBar) return
      
      const rect = progressBar.getBoundingClientRect()
      const clickX = event.clientX - rect.left
      const percent = clickX / rect.width
      const newTime = percent * this.duration
      
      this.audio.currentTime = Math.max(0, Math.min(newTime, this.duration))
      this.currentTime = this.audio.currentTime
    },

    // 开始拖拽进度条
    startDrag(event) {
      if (!this.audio || !this.duration) return
      
      this.isDragging = true
      
      const handleMouseMove = (e) => {
        const progressBar = this.$refs.progressBar
        if (!progressBar) return
        
        const rect = progressBar.getBoundingClientRect()
        const moveX = e.clientX - rect.left
        const percent = Math.max(0, Math.min(moveX / rect.width, 1))
        const newTime = percent * this.duration
        
        this.currentTime = newTime
      }
      
      const handleMouseUp = () => {
        if (this.audio) {
          this.audio.currentTime = this.currentTime
        }
        this.isDragging = false
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
      
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      
      event.preventDefault()
    },

    // 格式化音频时间显示（将秒数转换为 MM:SS 格式）
    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00'
      
      const mins = Math.floor(seconds / 60)
      const secs = Math.floor(seconds % 60)
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    },

    // 重置音频状态
    resetAudio() {
      // 清理音频对象和事件监听器
      this.cleanupAudio()
      
      // 重置音频状态
      this.isPlaying = false
      this.audioLoading = false
      this.currentTime = 0
      this.duration = 0
      this.isDragging = false
      // 注意：不在这里清空 audioInfo，由调用者决定是否设置新的音频
    },

    // 处理关键词点击事件（打开时间轴弹窗）
    handleKeywordTimelineClick(bookKeywordsId, keywordName) {
      if (!bookKeywordsId) {
        console.warn('bookKeywordsId 为空')
        return
      }
      console.log('点击关键词打开时间轴:', bookKeywordsId, keywordName)
      this.selectedBookKeywordsId = bookKeywordsId
      this.selectedKeywordName = keywordName || ''
      this.$nextTick(() => {
        this.showKeywordsTimelineDialog = true
        console.log('弹窗状态已设置为:', this.showKeywordsTimelineDialog)
        console.log('selectedBookKeywordsId:', this.selectedBookKeywordsId)
      })
    },

    // 处理关键词点击事件（根据当前模式决定行为）
    handleKeywordClick(bookKeywordsId, keyword) {
      // 如果已经在关键词搜索模式下，点击关键词打开时间轴弹窗
      if (this.isKeywordSearch) {
        this.handleKeywordTimelineClick(bookKeywordsId, keyword)
        return
      }
      
      // 否则，进入关键词搜索模式
      if (!bookKeywordsId) {
        console.warn('bookKeywordsId 为空')
        return
      }
      
      // 清除分类搜索状态
      this.isMajorSearch = false
      this.currentMajor = ''
      
      // 设置关键词搜索状态
      this.currentKeyword = keyword
      this.currentKeywordId = bookKeywordsId
      this.searchInput = keyword // 设置输入框内容
      this.isKeywordSearch = true
      this.showDatePopup = false // 关闭日期弹窗
      this.expandedNews = null // 关闭展开的新闻
      this.currentPage = 1
      this.hasMore = true
      this.newsList = []
      
      // 重置音频
      this.resetAudio()
      this.audioInfo = null
      this.globalAudioInfo = null
      
      this.loadKeywordNews(bookKeywordsId)
    },

    // 处理专业分类点击事件
    handleMajorClick(majorId, majorName) {
      // 清除关键词搜索状态
      this.isKeywordSearch = false
      this.currentKeyword = ''
      
      // 设置专业分类搜索状态
      this.currentMajor = majorId
      this.searchInput = majorName // 显示分类名称
      this.isMajorSearch = true
      this.showDatePopup = false // 关闭日期弹窗
      this.expandedNews = null // 关闭展开的新闻
      this.currentPage = 1
      this.hasMore = true
      this.newsList = []
      this.loadMajorNews(majorId)
    },

    // 执行搜索操作（点击搜索图标或按回车键）
    executeSearch() {
      const keyword = this.searchInput.trim()
      if (!keyword) {
        this.$message.warning('请输入搜索关键词')
        return
      }
      
      // 如果关键词与当前的相同，不重新搜索
      if (keyword === this.currentKeyword && this.currentKeywordId) {
        return
      }
      
      // 注意：搜索框输入的是关键词文本，但新接口需要 book_keywords_id
      // 这里暂时提示用户点击关键词标签进行搜索
      this.$message.warning('请点击新闻中的关键词标签进行搜索')
    },

    // 执行专业分类搜索操作
    executeMajorSearch() {
      const major = this.searchInput.trim()
      if (!major) {
        this.$message.warning('请输入专业分类名称')
        return
      }
      
      // 如果专业名称与当前的相同，不重新搜索
      if (major === this.currentMajor) {
        return
      }
      
      // 执行新的搜索
      this.currentMajor = major
      this.expandedNews = null
      this.currentPage = 1
      this.hasMore = true
      this.newsList = []
      this.loadMajorNews(major)
    },

    // 加载关键词相关新闻
    async loadKeywordNews(bookKeywordsId) {
      if (!bookKeywordsId || this.isLoadingMore) return
      
      this.loading = this.currentPage === 1
      this.isLoadingMore = this.currentPage > 1
      
      try {
        const params = {
          book_keywords_id: bookKeywordsId
        }
        const response = await getBookKeywordsTimeline(params)
        
        console.log('关键词新闻接口返回:', response)
        
        // 适配新接口结构: {code: 200, result: {list: [...], audio_info: null}, message: ""}
        // request.js在code=200时会直接返回result部分
        let newsList = []
        
        if (response && response.list && Array.isArray(response.list)) {
          // 新接口结构：response.list 是新闻数组
          newsList = response.list
          
          // 第一页时，如果有音频信息，存储到组件中
          if (this.currentPage === 1 && response.audio_info) {
            this.globalAudioInfo = response.audio_info
            // 如果没有展开的新闻，使用全局音频
            if (!this.expandedNews) {
              this.audioInfo = response.audio_info
            }
          }
        } else if (Array.isArray(response)) {
          // 兼容旧的接口结构（直接返回数组）
          newsList = response
        } else if (response && response.result && Array.isArray(response.result)) {
          // 兼容其他可能的结构
          newsList = response.result
        }
        
        console.log('解析后的新闻列表:', newsList)
        
        // 新接口返回全部数据，不需要分页
        this.hasMore = false
        
        // 设置新闻列表
        this.newsList = newsList
        
        // 默认打开第一条新闻
        if (this.newsList.length > 0) {
          const firstNews = this.newsList[0]
          this.expandedNews = firstNews.news_id
          // 检查第一条新闻的音频
          const audioUrl = firstNews.audio_mp3 || firstNews.audio_url
          if (audioUrl) {
            this.audioInfo = { audio_url: audioUrl }
            // 自动播放音频
            this.$nextTick(() => {
              this.initAudio(true)
            })
          } else {
            this.audioInfo = this.globalAudioInfo
            // 如果有全局音频，自动播放
            if (this.globalAudioInfo?.audio_url) {
              this.$nextTick(() => {
                this.initAudio(true)
              })
            }
          }
        }
        
      } catch (error) {
        console.error('加载关键词新闻失败:', error)
        this.newsList = []
        
        if (error.message && error.message.includes('1003')) {
          this.$message.error('登录已过期，但关键词搜索不应该需要登录认证')
        } else {
          this.$message.error('加载关键词新闻失败，请稍后重试')
        }
      } finally {
        this.loading = false
        this.isLoadingMore = false
      }
    },

    // 加载专业分类相关新闻
    async loadMajorNews(majorId) {
      if (!majorId || !this.hasMore || this.isLoadingMore) return
      
      this.loading = this.currentPage === 1
      this.isLoadingMore = this.currentPage > 1
      
      try {
        const params = {
          category_id: majorId,
          page: this.currentPage,
          limit: this.pageSize
        }
        const response = await getHistoryAiNewsList(params)
        
        // 适配接口结构: {code: 200, result: {list: [], audio_info: []}, message: ""}
        let newsList = []
        if (response && response.list && Array.isArray(response.list)) {
          newsList = response.list
          
          // 专业分类搜索模式下不显示音频信息
          if (this.currentPage === 1) {
          this.audioInfo = null
          }
        } else if (Array.isArray(response)) {
          // 兼容旧的接口结构（直接返回数组）
          newsList = response
        } else if (response && response.result && Array.isArray(response.result)) {
          // 兼容其他可能的结构
          newsList = response.result
        }
        
        // 判断是否还有更多数据
        if (newsList.length < this.pageSize) {
          this.hasMore = false
        }
        
        // 追加新闻到列表
        if (this.currentPage === 1) {
          this.newsList = newsList
          // 默认打开第一条新闻
          if (this.newsList.length > 0) {
            const firstNews = this.newsList[0]
            this.expandedNews = firstNews.news_id
            // 检查第一条新闻的音频
            const audioUrl = firstNews.audio_mp3 || firstNews.audio_url
            if (audioUrl) {
              this.audioInfo = { audio_url: audioUrl }
              // 自动播放音频
              this.$nextTick(() => {
                this.initAudio(true)
              })
            }
          }
        } else {
          this.newsList = [...this.newsList, ...newsList]
        }
        
      } catch (error) {
        if (this.currentPage === 1) {
        this.newsList = []
        }
        
        if (error.message && error.message.includes('1003')) {
          this.$message.error('登录已过期，但专业分类搜索不应该需要登录认证')
        } else {
          this.$message.error('网络错误，请稍后重试')
        }
      } finally {
        this.loading = false
        this.isLoadingMore = false
      }
    },

    // 退出关键词搜索模式
    exitKeywordSearch() {
      this.isKeywordSearch = false
      this.currentKeyword = ''
      this.currentKeywordId = null
      this.searchInput = '' // 清空搜索输入框
      this.expandedNews = null
      this.currentPage = 1
      this.hasMore = true
      this.newsList = []
      
      // 重置音频
      this.resetAudio()
      this.audioInfo = null
      this.globalAudioInfo = null
      
      this.loadNews() // 重新加载日期模式的新闻
    },

    // 退出专业分类搜索模式
    exitMajorSearch() {
      this.isMajorSearch = false
      this.currentMajor = ''
      this.searchInput = ''
      this.expandedNews = null
      this.currentPage = 1
      this.hasMore = true
      this.newsList = []
      this.loadNews() // 重新加载日期模式的新闻
    },

    // 添加滚动监听器
    addScrollListener() {
      this.$nextTick(() => {
        const newsContent = this.$refs.newsContent
        if (newsContent) {
          newsContent.addEventListener('scroll', this.handleScroll)
        }
      })
    },

    // 处理滚动事件
    handleScroll(event) {
      const element = event.target
      const scrollHeight = element.scrollHeight
      const scrollTop = element.scrollTop
      const clientHeight = element.clientHeight
      
      const distanceToBottom = scrollHeight - scrollTop - clientHeight
      
      // 当滚动到距离底部100px时，加载更多
      if (distanceToBottom < 100 && this.hasMore && !this.isLoadingMore && !this.loading) {
        this.currentPage++
        if (this.isKeywordSearch) {
          // 关键词搜索使用新接口，一次性返回所有数据，不需要分页
          // this.loadKeywordNews(this.currentKeywordId)
        } else if (this.isMajorSearch) {
          this.loadMajorNews(this.currentMajor)
        } else {
          this.loadNews()
        }
      }
    }
  },

  // 组件销毁时清理音频资源和滚动监听器
  beforeDestroy() {
    // 清理音频对象和事件监听器
    this.cleanupAudio()
    
    // 移除滚动监听器
    const newsContent = this.$refs.newsContent
    if (newsContent) {
      newsContent.removeEventListener('scroll', this.handleScroll)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/tech-news.scss';
</style>
